import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { api } from "../lib/api";
import {
  ALLE_CONTACTEN_API,
  PERSONEEL_API,
  VRIJWILLIGERS_API,
  contactListApiPath,
  contactModalTitle,
} from "../lib/contactApiPaths";
import {
  contactEmailMatchKeys,
  contactIsDeceased,
  contactPreferredEmail,
  formatContactDisplayName,
  normalizeUserEmailForMatch,
  userHasPermission,
  type Contact,
  type Group,
  type User,
} from "@domaddress/shared";
import { CopyTextButton, MapsLinkButton, formatLocationMapsQuery } from "../components/DetailFieldActions";
import ShareModal from "../components/ShareModal";
import SendWerkgroepIntakeModal from "../components/SendWerkgroepIntakeModal";
import ContactViewModal from "../components/ContactViewModal";
import SearchableSelect, { type SearchableSelectOption } from "../components/SearchableSelect";
import ChairMemberAddSearch, { type ChairMemberOption } from "../components/ChairMemberAddSearch";
import HelpHint from "../components/HelpHint";

/** Koppel medewerker (contact) aan app-gebruiker: expliciete koppeling ofzelfde e-mail (Dominicus → primair → secundair). */
function findUserForContact(c: Contact, users: User[]): User | undefined {
  const byId = users.find((u) => u.contact_id === c.id);
  if (byId) return byId;
  for (const k of contactEmailMatchKeys(c)) {
    const u = users.find((x) => normalizeUserEmailForMatch(x.email || "") === k);
    if (u) return u;
  }
  return undefined;
}

function byContactName(a: Contact, b: Contact) {
  const l = a.last_name.localeCompare(b.last_name, "nl");
  if (l !== 0) return l;
  return a.first_name.localeCompare(b.first_name, "nl");
}

/** Alleen vrijwilliger/personeel-kaarten: server weigert beheerder/kijker/voorzitter op andere typen (assertMedewerkerContactId). */
function isStaffAddressBookContact(c: Contact): boolean {
  return c.entity_kind === "vrijwilliger" || c.entity_kind === "personeel";
}

/** Kaart staat nog niet als vrijwilliger of personeel → bij toevoegen als groepslid moet je eerst kiezen. */
function contactNeedsStaffKindChoice(c: Contact): boolean {
  const k = c.entity_kind;
  return k !== "vrijwilliger" && k !== "personeel";
}

/** Tweede regel in zoekdropdowns: e-mail e.d. + "(overleden)" zodat overleden personen zichtbaar zijn en je op "overleden" kunt filteren. */
function deceasedExtraOnDescriptionLine(primary: string, c: Contact): string | undefined {
  if (!contactIsDeceased(c)) {
    const p = primary.trim();
    return p || undefined;
  }
  const p = primary.trim();
  return p ? `${p} (overleden)` : "(overleden)";
}

/** Groepeer de zoeklijst op adresboek-type (vrijwilliger, dominicusganger, …). */
function contactAddressBookGroupLabel(entityKind: string | null | undefined): string {
  switch (entityKind) {
    case "vrijwilliger":
      return "Vrijwilliger";
    case "personeel":
      return "Personeel";
    case "dominicusganger":
      return "Dominicusganger";
    case "extern":
      return "Extern contact";
    case "leverancier":
      return "Leverancier";
    default:
      return "Overig type";
  }
}

export default function GroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  type GroupContactRole = {
    contact_id: number;
    first_name: string;
    tussenvoegsel?: string | null;
    last_name: string;
    email: string | null;
  };

  const [group, setGroup] = useState<
    | (Group & {
        view_mode: "full";
        members: Contact[];
        admins: User[];
        viewers?: User[];
        chair?: User | null;
        admin_contacts?: GroupContactRole[];
        viewer_contacts?: GroupContactRole[];
        chair_contact?: GroupContactRole | null;
        secretary?: User | null;
        secretary_contact?: GroupContactRole | null;
        group_capabilities?: {
          is_chair: boolean;
          can_manage_members: boolean;
          can_manage_member_taken?: boolean;
          can_view_secretary?: boolean;
          can_manage_secretary?: boolean;
        };
      })
    | (Group & {
        view_mode: "directory";
        members: { id: number; first_name: string; tussenvoegsel: string | null; last_name: string }[];
        description: string | null;
        email: string | null;
        voorzitter_name: string | null;
      })
    | null
  >(null);
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [addContactId, setAddContactId] = useState("");
  const [addAdminId, setAddAdminId] = useState("");
  const [addViewerId, setAddViewerId] = useState("");
  const [addChairId, setAddChairId] = useState("");
  const [reqOpen, setReqOpen] = useState<{ id: number; name: string } | null>(null);
  const [reqMessage, setReqMessage] = useState("");
  const [reqSending, setReqSending] = useState(false);
  const [reqDone, setReqDone] = useState("");
  const [chairAddBusyId, setChairAddBusyId] = useState<number | null>(null);
  const [chairAddMsg, setChairAddMsg] = useState("");
  /** Zelfde bekijk-modal als op de adresboek-lijsten. */
  const [memberView, setMemberView] = useState<Contact | null>(null);
  const [memberTakenRows, setMemberTakenRows] = useState<{ contact_id: number; taken_id: number; name: string }[]>([]);
  const [allTaken, setAllTaken] = useState<{ id: number; name: string }[]>([]);
  const [taakInputByMember, setTaakInputByMember] = useState<Record<number, string>>({});
  const [taakBusyId, setTaakBusyId] = useState<number | null>(null);
  const [showIntakeModal, setShowIntakeModal] = useState(false);
  /** Persoon zonder vrijwilliger/personeel-kaart: eerst rol kiezen in modal. */
  const [addLidKindModal, setAddLidKindModal] = useState<Contact | null>(null);
  const [ledenAddBusy, setLedenAddBusy] = useState(false);
  const [ledenAddMsg, setLedenAddMsg] = useState("");
  /** Voorzitter kiezen: kaart is nog geen vrijwilliger/personeel → eerst rol kiezen (zelfde als bij leden). */
  const [chairPromotePending, setChairPromotePending] = useState<{
    contactForLabel: Contact;
    payload: { contact_id?: number; user_id?: number };
  } | null>(null);
  const [chairSetBusy, setChairSetBusy] = useState(false);
  const [addSecretaryId, setAddSecretaryId] = useState("");
  const [secretarySetBusy, setSecretarySetBusy] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [id]);

  /** Rijen medewerker + eventueel user: volgorde = medewerker-lijst; kiezers volgen dezelfde volgorde. */
  const {
    nonMembers,
    adminUserOptions,
    adminOnlyContact,
    chairUserOptions,
    chairOnlyContact,
    viewerUserOptions,
    viewerOnlyContact,
  } = useMemo(() => {
      if (!group || group.view_mode === "directory") {
        return {
          nonMembers: [] as Contact[],
          adminUserOptions: [] as { c: Contact; u: User }[],
          adminOnlyContact: [] as Contact[],
          chairUserOptions: [] as { c: Contact; u: User }[],
          chairOnlyContact: [] as Contact[],
          viewerUserOptions: [] as { c: Contact; u: User }[],
          viewerOnlyContact: [] as Contact[],
        };
      }
      const memberIds = new Set(group.members?.map((m) => m.id) ?? []);
      const nonMembers = allContacts.filter((c) => !memberIds.has(c.id));

      const sortedStaff = allContacts.filter(isStaffAddressBookContact).sort(byContactName);
      const staffRows: { c: Contact; u?: User }[] = sortedStaff.map((c) => ({
        c,
        u: findUserForContact(c, allUsers),
      }));

      const sortedAll = [...allContacts].sort(byContactName);
      const allRows: { c: Contact; u?: User }[] = sortedAll.map((c) => ({
        c,
        u: findUserForContact(c, allUsers),
      }));

      const adminIds = new Set(group.admins?.map((a) => a.id) ?? []);
      const adminContactIds = new Set(group.admin_contacts?.map((a) => a.contact_id) ?? []);
      const viewerIds = new Set(group.viewers?.map((v) => v.id) ?? []);
      const viewerContactIds = new Set(group.viewer_contacts?.map((v) => v.contact_id) ?? []);
      const chairId = group.chair?.id;
      const chairContactId = group.chair_contact?.contact_id;

      const isAlreadyGroupAdmin = (c: Contact, u: User | undefined) =>
        (u && adminIds.has(u.id)) || adminContactIds.has(c.id);
      const isAlreadyViewer = (c: Contact, u: User | undefined) =>
        (u && viewerIds.has(u.id)) || viewerContactIds.has(c.id);
      const isAlreadyChair = (c: Contact, u: User | undefined) =>
        (u && u.id === chairId) || c.id === chairContactId;

      const adminUserOptions: { c: Contact; u: User }[] = [];
      const adminOnlyContact: Contact[] = [];
      const chairUserOptions: { c: Contact; u: User }[] = [];
      const chairOnlyContact: Contact[] = [];
      const viewerUserOptions: { c: Contact; u: User }[] = [];
      const viewerOnlyContact: Contact[] = [];

      for (const row of staffRows) {
        const { c, u } = row;
        if (u) {
          if (!isAlreadyGroupAdmin(c, u)) adminUserOptions.push({ c, u });
          if (!isAlreadyGroupAdmin(c, u) && !isAlreadyViewer(c, u)) viewerUserOptions.push({ c, u });
        } else {
          if (!isAlreadyGroupAdmin(c, undefined)) adminOnlyContact.push(c);
          if (!isAlreadyGroupAdmin(c, undefined) && !isAlreadyViewer(c, undefined)) viewerOnlyContact.push(c);
        }
      }

      for (const row of allRows) {
        const { c, u } = row;
        if (u) {
          if (!isAlreadyChair(c, u)) chairUserOptions.push({ c, u });
        } else {
          if (!isAlreadyChair(c, undefined)) chairOnlyContact.push(c);
        }
      }

      return {
        nonMembers,
        adminUserOptions,
        adminOnlyContact,
        chairUserOptions,
        chairOnlyContact,
        viewerUserOptions,
        viewerOnlyContact,
      };
    }, [group, allContacts, allUsers]);

  const ledenZoekOpties = useMemo((): SearchableSelectOption[] => {
    return nonMembers.map((c) => {
      const heeftInlog = Boolean(findUserForContact(c, allUsers));
      const emailPart = contactPreferredEmail(c) || "";
      const inlogPart = heeftInlog ? "met app-account" : "geen app-account";
      const descParts = [emailPart, inlogPart, contactIsDeceased(c) ? "(overleden)" : ""].filter(Boolean);
      return {
        value: String(c.id),
        label: formatContactDisplayName(c),
        description: descParts.length ? descParts.join(" · ") : undefined,
        group: contactAddressBookGroupLabel(c.entity_kind),
      };
    });
  }, [nonMembers, allUsers]);

  const chairLedenZoekOpties = useMemo((): ChairMemberOption[] => {
    return [...nonMembers]
      .sort(byContactName)
      .map((c) => {
        const heeftInlog = Boolean(findUserForContact(c, allUsers));
        const deceased = contactIsDeceased(c);
        return {
          id: c.id,
          label: deceased ? `${formatContactDisplayName(c)} (overleden)` : formatContactDisplayName(c),
          group: heeftInlog ? "Met app-account" : "Nog geen app-account",
        };
      });
  }, [nonMembers, allUsers]);

  // Elke regel: unieke c:contactId (zelfde u.id op 2 kaarten gaf vroeger dubbele `value`)
  const chairZoekOpties = useMemo((): SearchableSelectOption[] => {
    const a = chairUserOptions.map(({ c, u }) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(u.email ?? "", c),
      group: `${contactAddressBookGroupLabel(c.entity_kind)} · met app-account` as const,
    }));
    const b = chairOnlyContact.map((c) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(contactPreferredEmail(c) || "", c),
      group: `${contactAddressBookGroupLabel(c.entity_kind)} · zonder app-account` as const,
    }));
    return [...a, ...b];
  }, [chairUserOptions, chairOnlyContact]);

  const adminZoekOpties = useMemo((): SearchableSelectOption[] => {
    const a = adminUserOptions.map(({ c, u }) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(u.email ?? "", c),
      group: "Met app-account" as const,
    }));
    const b = adminOnlyContact.map((c) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(contactPreferredEmail(c) || "", c),
      group: "Alleen medewerker-kaart (geen inlog)" as const,
    }));
    return [...a, ...b];
  }, [adminUserOptions, adminOnlyContact]);

  const viewerZoekOpties = useMemo((): SearchableSelectOption[] => {
    const a = viewerUserOptions.map(({ c, u }) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(u.email ?? "", c),
      group: "Met app-account" as const,
    }));
    const b = viewerOnlyContact.map((c) => ({
      value: `c:${c.id}`,
      label: formatContactDisplayName(c),
      description: deceasedExtraOnDescriptionLine(contactPreferredEmail(c) || "", c),
      group: "Alleen medewerker-kaart (geen inlog)" as const,
    }));
    return [...a, ...b];
  }, [viewerUserOptions, viewerOnlyContact]);

  /** mailto:-lijst voor alle leden-e-mailadressen (dedupe; ingekort bij zeer lange URL). */
  const mailtoAlleLeden = useMemo((): { href: string | null; total: number; included: number } | null => {
    if (!group || group.view_mode !== "full" || !group.members?.length) return null;
    const emails: string[] = [];
    const seen = new Set<string>();
    for (const m of group.members) {
      const e = contactPreferredEmail(m);
      if (!e) continue;
      const k = e.toLowerCase();
      if (seen.has(k)) continue;
      seen.add(k);
      emails.push(e);
    }
    if (emails.length === 0) return null;
    const MAX_LEN = 1700;
    let n = emails.length;
    while (n > 0 && `mailto:${emails.slice(0, n).join(",")}`.length > MAX_LEN) n--;
    const href = n > 0 ? `mailto:${emails.slice(0, n).join(",")}` : null;
    return { href, total: emails.length, included: n };
  }, [group]);

  const reloadMemberTaken = useCallback(async () => {
    if (!id) return;
    try {
      const [takenRes, takenListRes] = await Promise.all([
        api.get<{ data: { contact_id: number; taken_id: number; name: string }[] }>(`/api/groups/${id}/member-taken`),
        api.get<{ data: { id: number; name: string }[] }>("/api/taken"),
      ]);
      setMemberTakenRows(takenRes.data.data ?? []);
      setAllTaken(takenListRes.data.data ?? []);
    } catch {
      setMemberTakenRows([]);
      setAllTaken([]);
    }
  }, [id]);

  async function fetchAll() {
    setLoading(true);
    try {
      setReqDone("");
      setLedenAddMsg("");
      const gr = await api.get(`/api/groups/${id}`);
      const data = gr.data.data;
      if (data.view_mode === "directory") {
        setGroup(data);
        setAllContacts([]);
        setAllUsers([]);
        setMemberTakenRows([]);
        setAllTaken([]);
        const meRes = await api.get("/api/me");
        setCurrentUser(meRes.data.data);
        return;
      }

      const [usersRes, meRes] = await Promise.all([api.get("/api/users"), api.get("/api/me")]);
      const meData = meRes.data.data as User;
      setGroup(data);

      const admins = data.admins;
      const adminContacts = data.admin_contacts;
      const canManageLedenPool =
        Boolean(meData?.is_super_admin) ||
        meData?.app_role === "secretaris" ||
        meData?.app_role === "beheerder" ||
        Boolean(admins?.some((a: User) => a.id === meData.id)) ||
        Boolean(
          meData?.contact_id != null &&
            adminContacts?.some((x: { contact_id: number }) => x.contact_id === meData.contact_id)
        );

      let merged: Contact[] = [];
      if (canManageLedenPool) {
        const allRes = await api.get<{ data: Contact[] }>(ALLE_CONTACTEN_API, {
          params: { for_group_id: id },
        });
        merged = [...(allRes.data.data ?? [])].sort(byContactName);
      } else {
        const groupParams = { for_group_id: id };
        const [vrijRes, persRes] = await Promise.all([
          api.get(VRIJWILLIGERS_API, { params: groupParams }),
          api.get(PERSONEEL_API, { params: groupParams }),
        ]);
        const byId = new Map<number, Contact>();
        for (const c of vrijRes.data.data as Contact[]) byId.set(c.id, c);
        for (const c of persRes.data.data as Contact[]) byId.set(c.id, c);
        merged = [...byId.values()].sort(byContactName);
      }
      setAllContacts(merged);
      setAllUsers(usersRes.data.data);
      setCurrentUser(meData);
      await reloadMemberTaken();
    } finally {
      setLoading(false);
    }
  }

  async function submitDetailRequest() {
    if (!reqOpen || !id) return;
    setReqSending(true);
    setReqDone("");
    try {
      await api.post(`/api/groups/${id}/member-detail-requests`, {
        target_contact_id: reqOpen.id,
        message: reqMessage.trim() || undefined,
      });
      setReqDone("Verzoek verstuurd. Voorzitter en secretaris krijgen bericht (e-mail) en het staat in Aanvragen.");
      setReqMessage("");
      setReqOpen(null);
    } catch (e) {
      setReqDone("Versturen mislukt. Probeer later opnieuw of neem rechtstreeks contact op met de groep.");
    } finally {
      setReqSending(false);
    }
  }

  const isGroupAdmin = Boolean(
    group &&
      group.view_mode === "full" &&
      currentUser &&
      (group.admins?.some((a) => a.id === currentUser.id) ||
        (currentUser.contact_id != null &&
          group.admin_contacts?.some((x) => x.contact_id === currentUser.contact_id)))
  );
  const r = currentUser?.app_role;
  const canManageMembers =
    isGroupAdmin ||
    !!currentUser?.is_super_admin ||
    userHasPermission(currentUser, "groups_manage_members_app") ||
    userHasPermission(currentUser, "groups_manage_members");
  /** Serverbron: o.a. secretaris op medewerkerkaart bij meerdere accounts (`app_role` op user kan `deelnemer` zijn). */
  const canEditMemberTaken =
    Boolean(
      group &&
        group.view_mode === "full" &&
        (group.group_capabilities?.can_manage_member_taken ??
          group.group_capabilities?.can_manage_members)
    ) ||
    canManageMembers;
  const canChairRequestLeden =
    group &&
    group.view_mode === "full" &&
    group.group_capabilities?.is_chair &&
    !group.group_capabilities?.can_manage_members;
  const canSetChair = canManageMembers;
  const canViewSecretary = group?.view_mode === "full" && !!group.group_capabilities?.can_view_secretary;
  const canManageSecretary = group?.view_mode === "full" && !!group.group_capabilities?.can_manage_secretary;
  const canShare = Boolean(
    group &&
      group.view_mode === "full" &&
      currentUser &&
      (isGroupAdmin ||
        currentUser.is_super_admin ||
        (group.chair != null && group.chair.id === currentUser.id) ||
        (group.chair_contact != null &&
          currentUser.contact_id != null &&
          group.chair_contact.contact_id === currentUser.contact_id))
  );

  const isChairForGroup = Boolean(
    group && group.view_mode === "full" && group.group_capabilities?.is_chair
  );
  const isCurrentUserContactInLeden = Boolean(
    currentUser?.contact_id != null &&
      group &&
      group.view_mode === "full" &&
      (group.members ?? []).some((m) => m.id === currentUser.contact_id)
  );
  /** Alleen voorzitter, (super)admins, secretaris, of medewerkerkaart die bij Leden hoort — niet groepsbeheerder alleen. */
  const canShowBulkLedenEmail = Boolean(
    group &&
      group.view_mode === "full" &&
      currentUser &&
      (Boolean(currentUser.is_super_admin) ||
        r === "beheerder" ||
        r === "secretaris" ||
        isChairForGroup ||
        isCurrentUserContactInLeden)
  );

  async function performAddGroupMember(contactId: number, entityKindOnAdd?: "vrijwilliger" | "personeel") {
    if (!id) return;
    setLedenAddBusy(true);
    setLedenAddMsg("");
    try {
      await api.post(`/api/groups/${id}/contacts`, {
        contact_id: contactId,
        ...(entityKindOnAdd ? { entity_kind_on_add: entityKindOnAdd } : {}),
      });
      setAddContactId("");
      setAddLidKindModal(null);
      fetchAll();
    } catch (e: unknown) {
      let msg = "Toevoegen mislukt. Probeer het opnieuw.";
      if (axios.isAxiosError(e) && e.response?.data && typeof e.response.data === "object") {
        const d = e.response.data as { error?: string };
        if (d.error) msg = d.error;
      }
      setLedenAddMsg(msg);
    } finally {
      setLedenAddBusy(false);
    }
  }

  async function addContact() {
    if (!addContactId) return;
    const c = allContacts.find((x) => x.id === Number(addContactId));
    if (!c) return;
    if (contactNeedsStaffKindChoice(c)) {
      setLedenAddMsg("");
      setAddLidKindModal(c);
      return;
    }
    await performAddGroupMember(c.id);
  }

  async function chairRequestAddMember(contactId: number) {
    if (!id) return;
    setChairAddMsg("");
    setChairAddBusyId(contactId);
    try {
      await api.post(`/api/groups/${id}/member-add-requests`, {
        target_contact_id: contactId,
      });
      setChairAddMsg("ok:Verzoek verstuurd naar secretaris en beheerders. Na goedkeuring verschijnt de persoon bij Leden.");
    } catch {
      setChairAddMsg("err:Versturen mislukt. Mogelijk bestaat er al een open verzoek, of probeer later opnieuw.");
    } finally {
      setChairAddBusyId(null);
    }
  }

  async function removeContact(contactId: number) {
    await api.delete(`/api/groups/${id}/contacts/${contactId}`);
    fetchAll();
  }

  async function addAdmin() {
    if (!addAdminId) return;
    const body =
      addAdminId.startsWith("c:")
        ? { contact_id: Number(addAdminId.slice(2)) }
        : { user_id: Number(addAdminId) };
    await api.post(`/api/groups/${id}/admins`, body);
    setAddAdminId("");
    fetchAll();
  }

  async function removeAdmin(userId: number) {
    await api.delete(`/api/groups/${id}/admins/${userId}`);
    fetchAll();
  }

  async function removeAdminContact(contactId: number) {
    await api.delete(`/api/groups/${id}/admins/contacts/${contactId}`);
    fetchAll();
  }

  async function addViewer() {
    if (!addViewerId) return;
    const body =
      addViewerId.startsWith("c:")
        ? { contact_id: Number(addViewerId.slice(2)) }
        : { user_id: Number(addViewerId) };
    await api.post(`/api/groups/${id}/viewers`, body);
    setAddViewerId("");
    fetchAll();
  }

  async function removeViewer(userId: number) {
    await api.delete(`/api/groups/${id}/viewers/${userId}`);
    fetchAll();
  }

  async function removeViewerContact(contactId: number) {
    await api.delete(`/api/groups/${id}/viewers/contacts/${contactId}`);
    fetchAll();
  }

  async function performSetChairRequest(body: {
    contact_id?: number;
    user_id?: number;
    entity_kind_on_promote?: "vrijwilliger" | "personeel";
  }) {
    if (!id) return;
    setChairSetBusy(true);
    try {
      await api.put(`/api/groups/${id}/chair`, body);
      setAddChairId("");
      setChairPromotePending(null);
      fetchAll();
    } catch {
      /* 400 / netwerkfout — geen detail in productie-UI */
    } finally {
      setChairSetBusy(false);
    }
  }

  async function setChair() {
    if (!addChairId) return;
    if (addChairId.startsWith("c:")) {
      const cid = Number(addChairId.slice(2));
      const c = allContacts.find((x) => x.id === cid);
      if (c && contactNeedsStaffKindChoice(c)) {
        setChairPromotePending({ contactForLabel: c, payload: { contact_id: cid } });
        return;
      }
      await performSetChairRequest({ contact_id: cid });
      return;
    }
    const uid = Number(addChairId);
    if (!Number.isFinite(uid) || uid <= 0) return;
    const u = allUsers.find((x) => x.id === uid);
    const co = u?.contact_id != null ? allContacts.find((x) => x.id === u.contact_id) : undefined;
    if (co && contactNeedsStaffKindChoice(co)) {
      setChairPromotePending({ contactForLabel: co, payload: { user_id: uid } });
      return;
    }
    await performSetChairRequest({ user_id: uid });
  }

  async function clearChair() {
    await api.delete(`/api/groups/${id}/chair`);
    fetchAll();
  }

  async function performSetSecretaryRequest(body: { contact_id?: number; user_id?: number }) {
    if (!id) return;
    setSecretarySetBusy(true);
    try {
      await api.put(`/api/groups/${id}/secretary`, body);
      setAddSecretaryId("");
      fetchAll();
    } catch {
      /* stil */
    } finally {
      setSecretarySetBusy(false);
    }
  }

  async function setSecretary() {
    if (!addSecretaryId) return;
    if (addSecretaryId.startsWith("c:")) {
      const cid = Number(addSecretaryId.slice(2));
      await performSetSecretaryRequest({ contact_id: cid });
      return;
    }
    const uid = Number(addSecretaryId);
    if (!Number.isFinite(uid) || uid <= 0) return;
    await performSetSecretaryRequest({ user_id: uid });
  }

  async function clearSecretary() {
    await api.delete(`/api/groups/${id}/secretary`);
    fetchAll();
  }

  const takenByMember = useMemo(() => {
    const m = new Map<number, { id: number; name: string }[]>();
    for (const row of memberTakenRows) {
      const arr = m.get(row.contact_id) ?? [];
      arr.push({ id: row.taken_id, name: row.name });
      m.set(row.contact_id, arr);
    }
    return m;
  }, [memberTakenRows]);

  async function addTaakForMember(m: Contact) {
    const raw = (taakInputByMember[m.id] ?? "").trim();
    if (!raw || !id) return;
    setTaakBusyId(m.id);
    try {
      await api.post(`/api/groups/${id}/members/${m.id}/taken`, { name: raw });
      setTaakInputByMember((s) => ({ ...s, [m.id]: "" }));
      await reloadMemberTaken();
    } catch {
      /* stille fout */
    } finally {
      setTaakBusyId(null);
    }
  }

  async function removeTaakFromMember(contactId: number, takenId: number) {
    if (!id) return;
    try {
      await api.delete(`/api/groups/${id}/members/${contactId}/taken/${takenId}`);
      await reloadMemberTaken();
    } catch {
      /* stille fout */
    }
  }

  if (loading) return <div className="page"><div className="loading-spinner" /></div>;
  if (!group) return <div className="page"><p>Groep niet gevonden.</p></div>;

  if (group.view_mode === "directory") {
    return (
      <div className="page">
        <nav style={{ marginBottom: "0.75rem" }} aria-label="Terug naar groepen">
          <button type="button" className="btn-back" onClick={() => navigate("/groups")}>
            ← Groepen
          </button>
        </nav>
        <header className="page-header group-page-header">
          <div>
            <div className="page-title-row" style={{ marginBottom: 2 }}>
              <h2 className="page-title" style={{ margin: 0 }}>
                {group.name}
              </h2>
              <HelpHint label="Uitleg: directory-weergave">
                Je ziet <strong>alleen namen</strong> van leden. Voor e-mail of telefoon stuur je een verzoek naar
                voorzitter en secretaris; zij krijgen een e-mail (als ingesteld) en zien het in{" "}
                <Link to="/aanvragen">Aanvragen</Link>.
              </HelpHint>
            </div>
            {group.description && <p className="page-subtitle">{group.description}</p>}
            {group.voorzitter_name && (
              <p className="page-subtitle detail-hover-block">
                <strong>Voorzitter:</strong> <span>{group.voorzitter_name}</span>
                <span className="detail-inline-actions">
                  <CopyTextButton text={group.voorzitter_name} label="Voorzitter" compact />
                </span>
              </p>
            )}
            {group.email && (
              <p className="page-subtitle detail-hover-block" style={{ marginTop: "0.35rem" }}>
                <strong>Groep-e-mail:</strong>{" "}
                <a href={`mailto:${group.email}`}>{group.email}</a>
                <span className="detail-inline-actions">
                  <CopyTextButton text={group.email} label="Groep-e-mail" compact />
                </span>
              </p>
            )}
          </div>
        </header>
        {reqDone && <p className="page-subtitle" style={{ color: "var(--ok, #1a7f37)" }}>{reqDone}</p>}
        <div className="detail-section" style={{ maxWidth: 640 }}>
          <div className="section-title-row">
            <h3 className="section-title">Leden ({group.members.length})</h3>
            <HelpHint label="Uitleg: leden in directory">
              <strong>Leden</strong> in deze weergave: je ziet alleen namen. Gebruik per persoon{" "}
              <strong>“Vraag contactgegevens”</strong> om e-mail/telefoon te vragen. Voorzitter en secretaris zien het verzoek bij{" "}
              <Link to="/aanvragen">Aanvragen</Link>.
            </HelpHint>
          </div>
          <ul className="member-list">
            {group.members.map((m) => {
              const label = formatContactDisplayName(m);
              return (
                <li key={m.id} className="member-item">
                  <div className="member-avatar" style={{ background: "var(--surface-hover)" }}>
                    <span>
                      {(m.first_name[0] ?? "·") + (m.last_name[0] ?? "·")}
                    </span>
                  </div>
                  <div className="member-info">
                    <span className="member-name">{label}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setReqMessage("");
                      setReqOpen({ id: m.id, name: label });
                    }}
                  >
                    Vraag contactgegevens
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        {reqOpen && (
          <div
            className="modal-backdrop"
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setReqOpen(null)}
            onKeyDown={(e) => e.key === "Escape" && setReqOpen(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Verzoek contactgegevens"
          >
            <div
              className="detail-section"
              style={{ maxWidth: 480, margin: 16, padding: 24 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="section-title" style={{ marginTop: 0 }}>
                Stuur verzoek (voorzitter en secretaris)
              </h3>
              <p className="page-subtitle">Voor: {reqOpen.name}</p>
              <label>
                <span>Optioneel bericht:</span>
                <textarea
                  className="search-input"
                  style={{ display: "block", width: "100%", minHeight: 100, marginTop: 8 }}
                  value={reqMessage}
                  onChange={(e) => setReqMessage(e.target.value)}
                />
              </label>
              <p style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="btn-primary" disabled={reqSending} onClick={submitDetailRequest}>
                  {reqSending ? "Bezig…" : "Verstuur"}
                </button>
                <button type="button" className="btn-secondary" onClick={() => setReqOpen(null)}>
                  Annuleer
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <nav style={{ marginBottom: "0.75rem" }} aria-label="Terug naar groepen">
        <button type="button" className="btn-back" onClick={() => navigate("/groups")}>
          ← Groepen
        </button>
      </nav>
      <header className="page-header group-page-header">
        <div>
          <h2 className="page-title">{group.name}</h2>
          {group.description && <p className="page-subtitle">{group.description}</p>}
            {group.email && (
              <p className="page-subtitle detail-hover-block" style={{ marginTop: "0.35rem" }}>
                <strong>Groep-e-mail:</strong>{" "}
                <a href={`mailto:${group.email}`}>{group.email}</a>
                <span className="detail-inline-actions">
                  <CopyTextButton text={group.email} label="Groep-e-mail" compact />
                </span>
              </p>
            )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
          {canManageMembers && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowIntakeModal(true)}
              title="Stuur de voorzitter een uitnodiging om de ledenlijst in te vullen"
            >
              📋 Ledenlijst uitnodigen
            </button>
          )}
          {canShare && (
            <button className="btn-primary" onClick={() => setShowShare(true)}>
              Zend contacten
            </button>
          )}
        </div>
      </header>

      <div className="detail-grid detail-grid--group">
        <div className="detail-grid__main">
        {/* Members: pure contacten — geen Microsoft vereist */}
        <div className="detail-section">
          <div className="section-title-row">
            <h3 className="section-title">Leden ({group.members?.length ?? 0})</h3>
            <HelpHint label="Uitleg: groepsleden">
              <strong>Leden</strong> zijn personen die aan de groep gekoppeld zijn (in de praktijk meestal uit het{" "}
              <strong>vrijwilligers- en personeelsadresboek</strong>). <strong>Secretaris, beheerder of groepsbeheerder</strong>{" "}
              kan iedereen uit het volledige adresboek zoeken; staat iemand nog als dominicusganger, extern enz., dan vraagt
              het systeem of ze als <strong>vrijwilliger</strong> of <strong>personeel</strong> in het medewerkersbestand
              komen (het vorige type blijft als <strong>extra label</strong> op de kaart). Als je alleen <strong>voorzitter</strong> bent, zie je alleen namen: je stuurt een verzoek; na goedkeuring
              wordt iemand lid. Voor <strong>beheerder, kijker of voorzitter</strong> kun je ook{" "}
              <strong>alleen de medewerker-kaart</strong> kiezen: zodra die persoon inlogt en aan dezelfde kaart wordt gekoppeld,
              gelden de rechten automatisch.
            </HelpHint>
            <HelpHint label="Uitleg: taken bij leden">
              <strong>Taken</strong> zijn korte rollen of klussen binnen deze groep (bijv. “Roostermaker”). Namen worden
              hergebruikt in de hele applicatie. <strong>Secretaris, beheerder of groepsbeheerder</strong> kan ze toewijzen
              (ook als je rol vooral op de medewerkerkaart staat); iedereen met volledige groepsweergave ziet ze.
              Bij elk lid: <strong>typ een naam</strong> in het veld (of kies een bestaande uit de lijst), daarna{" "}
              <strong>+ Taak</strong> of <strong>Enter</strong>. Je mag ook eerst op + Taak klikken — dan springt de cursor
              in het veld.
            </HelpHint>
            {canShowBulkLedenEmail && mailtoAlleLeden?.href && (
              <a
                href={mailtoAlleLeden.href}
                className="btn-secondary"
                style={{ fontSize: "0.8rem", padding: "0.35rem 0.75rem", marginLeft: "auto" }}
                title={
                  mailtoAlleLeden.included < mailtoAlleLeden.total
                    ? `Eerste ${mailtoAlleLeden.included} van ${mailtoAlleLeden.total} adressen (browserlimiet); voeg de rest handmatig toe.`
                    : `Standaard e-mailprogramma openen met ${mailtoAlleLeden.included} ontvanger(s) in het veld Aan`
                }
                onClick={(e) => e.stopPropagation()}
              >
                E-mail alle leden
              </a>
            )}
          </div>
          {chairAddMsg && (
            <p
              className="page-subtitle"
              style={{
                marginBottom: "0.5rem",
                color: chairAddMsg.startsWith("err:") ? "var(--danger, #b00020)" : "var(--ok, #1a7f37)",
              }}
            >
              {chairAddMsg.replace(/^(ok|err):/, "")}
            </p>
          )}
          {ledenAddMsg && canManageMembers && !addLidKindModal && (
            <p
              className="page-subtitle"
              style={{ marginBottom: "0.5rem", color: "var(--danger, #b00020)" }}
              role="alert"
            >
              {ledenAddMsg}
            </p>
          )}
          {canManageMembers && (
            <div className="add-row">
              <SearchableSelect
                value={addContactId}
                onValueChange={setAddContactId}
                options={ledenZoekOpties}
                placeholder="Zoek in het hele adresboek…"
                emptyMessage="Geen overeenkomst — probeer een andere naam of e-mail."
                aria-label="Contact toevoegen als groepslid"
              />
              <button
                className="btn-secondary"
                onClick={() => void addContact()}
                disabled={!addContactId || ledenAddBusy}
              >
                {ledenAddBusy ? "Bezig…" : "Toevoegen"}
              </button>
            </div>
          )}
          {canChairRequestLeden && (
            <div className="add-row" style={{ flexWrap: "wrap" as const, alignItems: "flex-start" }}>
              <ChairMemberAddSearch
                options={chairLedenZoekOpties}
                placeholder="Zoek medewerker op naam (alleen namen zichtbaar)…"
                emptyMessage="Geen overeenkomst — of iedereen is al lid."
                onRequestAdd={chairRequestAddMember}
                busyContactId={chairAddBusyId}
              />
            </div>
          )}
          <datalist id={id ? `group-taken-dl-${id}` : "group-taken-dl"}>
            {allTaken.map((t) => (
              <option key={t.id} value={t.name} />
            ))}
          </datalist>
          <ul className="member-list">
            {group.members?.map((m) => {
              const heeftInlog = Boolean(findUserForContact(m, allUsers));
              const takenLijst = takenByMember.get(m.id) ?? [];
              const memberPrefEmail = contactPreferredEmail(m);
              return (
                <li
                  key={m.id}
                  className="member-item"
                  style={{ flexWrap: "wrap", alignItems: "flex-start", rowGap: 8 }}
                >
                  <div className="member-avatar">
                    {m.profile_picture
                      ? <img src={`${import.meta.env.VITE_API_URL}${m.profile_picture}`} alt={formatContactDisplayName(m)} />
                      : <span>{m.first_name[0]}{m.last_name[0]}</span>
                    }
                  </div>
                  <div className="member-info">
                    <span className="member-name">{formatContactDisplayName(m)}</span>
                    {memberPrefEmail && (
                        <span className="detail-hover-row member-email" style={{ marginTop: 2 }}>
                          <a href={`mailto:${memberPrefEmail}`} onClick={(e) => e.stopPropagation()}>
                            {memberPrefEmail}
                          </a>
                          <span className="detail-inline-actions">
                            <CopyTextButton text={memberPrefEmail} label="E-mail" compact />
                          </span>
                        </span>
                    )}
                    {m.phone && (
                      <span className="detail-hover-row" style={{ marginTop: 4 }}>
                        <a href={`tel:${m.phone}`} onClick={(e) => e.stopPropagation()}>
                          {m.phone}
                        </a>
                        <span className="data-list-row-muted" style={{ fontSize: "0.85em" }}>
                          (vast)
                        </span>
                        <span className="detail-inline-actions">
                          <CopyTextButton text={m.phone} label="Telefoon vast" compact />
                        </span>
                      </span>
                    )}
                    {m.phone_mobile && (
                      <span className="detail-hover-row" style={{ marginTop: 4 }}>
                        <a href={`tel:${m.phone_mobile}`} onClick={(e) => e.stopPropagation()}>
                          {m.phone_mobile}
                        </a>
                        <span className="data-list-row-muted" style={{ fontSize: "0.85em" }}>
                          (mobiel)
                        </span>
                        <span className="detail-inline-actions">
                          <CopyTextButton text={m.phone_mobile} label="Telefoon mobiel" compact />
                        </span>
                      </span>
                    )}
                    {(() => {
                      const mq = formatLocationMapsQuery(m);
                      return mq ? (
                        <span className="detail-hover-row detail-hover-row--stretch" style={{ marginTop: 4 }}>
                          <span className="detail-inline-actions">
                            <MapsLinkButton query={mq} compact />
                          </span>
                        </span>
                      ) : null;
                    })()}
                    {!heeftInlog && (
                      <span
                        className="group-badge"
                        style={{ marginTop: 4, display: "inline-block", fontSize: "0.7rem" }}
                        title="Nog geen Microsoft-inlog; kan later alsnog in Beheer worden gekoppeld"
                      >
                        Nog geen app-account
                      </span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                      onClick={() => setMemberView(m)}
                    >
                      Kaart
                    </button>
                    {canManageMembers && (
                      <button className="btn-danger-sm" onClick={() => removeContact(m.id)}>✕</button>
                    )}
                  </div>
                  <div
                    style={{
                      flex: "1 1 100%",
                      minWidth: "100%",
                      paddingLeft: 46,
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                    }}
                  >
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                      {takenLijst.length === 0 && (
                        <span style={{ fontSize: "0.78rem", color: "var(--text-400)" }}>Geen taken</span>
                      )}
                      {takenLijst.map((t) => (
                        <span
                          key={t.id}
                          className="group-badge"
                          style={{ fontSize: "0.75rem", display: "inline-flex", alignItems: "center", gap: 4 }}
                        >
                          {t.name}
                          {canEditMemberTaken && (
                            <button
                              type="button"
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "0 2px",
                                lineHeight: 1,
                                color: "inherit",
                                opacity: 0.75,
                              }}
                              title={`Taak “${t.name}” loskoppelen`}
                              onClick={() => removeTaakFromMember(m.id, t.id)}
                              aria-label={`Verwijder taak ${t.name}`}
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))}
                    </div>
                    {canEditMemberTaken && (
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}
                        title="Typ een taaknaam in het veld (of kies uit de lijst) en druk op + Taak of Enter."
                      >
                        <input
                          id={`group-member-taak-input-${m.id}`}
                          type="text"
                          list={id ? `group-taken-dl-${id}` : undefined}
                          placeholder="Taaknaam typen of kiezen…"
                          maxLength={120}
                          value={taakInputByMember[m.id] ?? ""}
                          onChange={(e) =>
                            setTaakInputByMember((s) => ({ ...s, [m.id]: e.target.value }))
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addTaakForMember(m);
                            }
                          }}
                          className="search-input"
                          style={{ fontSize: "0.82rem", padding: "0.35rem 0.55rem", flex: "1 1 140px", minWidth: 0 }}
                          aria-label={`Taak voor ${formatContactDisplayName(m)}`}
                        />
                        <button
                          type="button"
                          className="btn-secondary"
                          style={{ fontSize: "0.75rem", padding: "0.3rem 0.65rem" }}
                          disabled={taakBusyId === m.id}
                          title={
                            (taakInputByMember[m.id] ?? "").trim()
                              ? "Taak toevoegen aan dit lid"
                              : "Eerst een taaknaam in het veld typen — of klik om het veld te focussen"
                          }
                          onClick={() => {
                            const v = (taakInputByMember[m.id] ?? "").trim();
                            if (!v) {
                              document.getElementById(`group-member-taak-input-${m.id}`)?.focus();
                              return;
                            }
                            void addTaakForMember(m);
                          }}
                        >
                          {taakBusyId === m.id ? "…" : "+ Taak"}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        </div>

        <div className="detail-grid__aside">
        {/* Voorzitter: user of medewerker-kaart */}
        <div className="detail-section">
          <div className="section-title-row">
            <h3 className="section-title">Voorzitter</h3>
            <HelpHint label="Uitleg: voorzitter in groep">
              Eén persoon, ook als die al in <strong>Leden</strong> zit, of (voorheen) als <strong>beheer</strong> voor
              deze groep: bij benoemen tot voorzitter vervalt in die groep de beheer-rol, zodat het niet dubbel staat.
              U kunt iedereen uit het adresboek kiezen; staat iemand nog niet als vrijwilliger of personeel, dan vraagt
              het systeem om eerst die rol te kiezen (zelfde als bij leden toevoegen).
            </HelpHint>
          </div>
          {canSetChair && (
            <div className="add-row">
              <SearchableSelect
                value={addChairId}
                onValueChange={setAddChairId}
                options={chairZoekOpties}
                placeholder={
                  group.chair || group.chair_contact ? "Anderen voorzitter kiezen…" : "Kies voorzitter…"
                }
                emptyMessage="Geen overeenkomst — of iedereen die past heeft al een andere groepsrol (bijv. al voorzitter)."
                aria-label="Voorzitter kiezen"
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => void setChair()}
                disabled={!addChairId || chairSetBusy}
              >
                {chairSetBusy ? "Bezig…" : "Instellen"}
              </button>
            </div>
          )}
          {group.chair && (
            <ul className="member-list">
              <li className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>
                    {group.chair.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                </div>
                <div className="member-info">
                  <span className="member-name">{group.chair.name}</span>
                  <span className="member-email">{group.chair.email}</span>
                </div>
                {canSetChair && (
                  <button type="button" className="btn-danger-sm" onClick={clearChair}>
                    ✕
                  </button>
                )}
                {currentUser && group.chair.id === currentUser.id && <span className="badge-you">Jij</span>}
              </li>
            </ul>
          )}
          {group.chair_contact && !group.chair && (
            <ul className="member-list">
              <li className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>
                    {(group.chair_contact.first_name[0] ?? "") + (group.chair_contact.last_name[0] ?? "")}
                  </span>
                </div>
                <div className="member-info">
                  <span className="member-name">
                    {formatContactDisplayName(group.chair_contact)}
                  </span>
                  {group.chair_contact.email && <span className="member-email">{group.chair_contact.email}</span>}
                  <span
                    className="group-badge"
                    style={{ marginTop: 4, display: "inline-block", fontSize: "0.7rem" }}
                    title="Rol op medewerker-kaart; zodra ingelogd met koppeling gelden dezelfde rechten"
                  >
                    Alleen medewerker-kaart
                  </span>
                </div>
                {canSetChair && (
                  <button type="button" className="btn-danger-sm" onClick={clearChair}>
                    ✕
                  </button>
                )}
                {currentUser &&
                  currentUser.contact_id != null &&
                  group.chair_contact.contact_id === currentUser.contact_id && <span className="badge-you">Jij (kaart)</span>}
              </li>
            </ul>
          )}
        </div>

        {canViewSecretary && group.view_mode === "full" && (
        <div className="detail-section">
          <div className="section-title-row">
            <h3 className="section-title">Groepssecretaris</h3>
            <HelpHint label="Uitleg: groepssecretaris">
              Eén persoon die als secretaris van deze werkgroep geldt. Dit blok is alleen zichtbaar als jouw app-rol dat recht heeft.
              Wijzigen mag wie groepsleden beheert of expliciet groepssecretaris mag instellen.
            </HelpHint>
          </div>
          {canManageSecretary && (
            <div className="add-row">
              <SearchableSelect
                value={addSecretaryId}
                onValueChange={setAddSecretaryId}
                options={chairZoekOpties}
                placeholder={
                  group.secretary || group.secretary_contact ? "Andere groepssecretaris kiezen…" : "Kies groepssecretaris…"
                }
                emptyMessage="Geen overeenkomst."
                aria-label="Groepssecretaris kiezen"
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => void setSecretary()}
                disabled={!addSecretaryId || secretarySetBusy}
              >
                {secretarySetBusy ? "Bezig…" : "Instellen"}
              </button>
            </div>
          )}
          {group.secretary && (
            <ul className="member-list">
              <li className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>
                    {group.secretary.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                </div>
                <div className="member-info">
                  <span className="member-name">{group.secretary.name}</span>
                  <span className="member-email">{group.secretary.email}</span>
                </div>
                {canManageSecretary && (
                  <button type="button" className="btn-danger-sm" onClick={() => void clearSecretary()}>
                    ✕
                  </button>
                )}
              </li>
            </ul>
          )}
          {group.secretary_contact && !group.secretary && (
            <ul className="member-list">
              <li className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>
                    {(group.secretary_contact.first_name[0] ?? "") + (group.secretary_contact.last_name[0] ?? "")}
                  </span>
                </div>
                <div className="member-info">
                  <span className="member-name">{formatContactDisplayName(group.secretary_contact)}</span>
                  {group.secretary_contact.email && (
                    <span className="member-email">{group.secretary_contact.email}</span>
                  )}
                </div>
                {canManageSecretary && (
                  <button type="button" className="btn-danger-sm" onClick={() => void clearSecretary()}>
                    ✕
                  </button>
                )}
              </li>
            </ul>
          )}
        </div>
        )}

        {/* Admins */}
        <div className="detail-section">
          <h3 className="section-title">
            Beheerders (
            {(group.admins?.length ?? 0) + (group.admin_contacts?.length ?? 0)}
            )
          </h3>
          {canManageMembers && (
            <div className="add-row">
              <SearchableSelect
                value={addAdminId}
                onValueChange={setAddAdminId}
                options={adminZoekOpties}
                placeholder="Beheerder toevoegen…"
                emptyMessage="Geen overeenkomst — of deze persoon is al beheerder."
                aria-label="Groepsbeheerder toevoegen"
              />
              <button className="btn-secondary" onClick={addAdmin} disabled={!addAdminId}>
                Toevoegen
              </button>
            </div>
          )}
          <ul className="member-list">
            {group.admins?.map((a) => (
              <li key={`u-${a.id}`} className="member-item">
                <div className="member-avatar admin-avatar">
                  <span>{a.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                </div>
                <div className="member-info">
                  <span className="member-name">{a.name}</span>
                  <span className="member-email">{a.email}</span>
                </div>
                {canManageMembers && a.id !== currentUser?.id && (
                  <button className="btn-danger-sm" onClick={() => removeAdmin(a.id)}>✕</button>
                )}
                {a.id === currentUser?.id && <span className="badge-you">Jij</span>}
              </li>
            ))}
            {group.admin_contacts?.map((ac) => (
              <li key={`c-${ac.contact_id}`} className="member-item">
                <div className="member-avatar admin-avatar">
                  <span>{(ac.first_name[0] ?? "") + (ac.last_name[0] ?? "")}</span>
                </div>
                <div className="member-info">
                  <span className="member-name">
                    {formatContactDisplayName(ac)}
                  </span>
                  {ac.email && <span className="member-email">{ac.email}</span>}
                  <span
                    className="group-badge"
                    style={{ marginTop: 4, display: "inline-block", fontSize: "0.7rem" }}
                  >
                    Alleen medewerker-kaart
                  </span>
                </div>
                {canManageMembers && (
                  <button
                    className="btn-danger-sm"
                    onClick={() => removeAdminContact(ac.contact_id)}
                  >✕</button>
                )}
                {currentUser &&
                  currentUser.contact_id != null &&
                  ac.contact_id === currentUser.contact_id && <span className="badge-you">Jij (kaart)</span>}
              </li>
            ))}
          </ul>
        </div>

        {/* Kijkers */}
        <div className="detail-section">
          <h3 className="section-title">
            Kijkers (
            {(group.viewers?.length ?? 0) + (group.viewer_contacts?.length ?? 0)}
            )
          </h3>
          {canManageMembers && (
            <div className="add-row">
              <SearchableSelect
                value={addViewerId}
                onValueChange={setAddViewerId}
                options={viewerZoekOpties}
                placeholder="Kijker toevoegen…"
                emptyMessage="Geen overeenkomst — of deze persoon is al kijker."
                aria-label="Groepskijker toevoegen"
              />
              <button className="btn-secondary" onClick={addViewer} disabled={!addViewerId}>
                Toevoegen
              </button>
            </div>
          )}
          <ul className="member-list">
            {group.viewers?.map((v) => (
              <li key={`u-${v.id}`} className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>{v.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}</span>
                </div>
                <div className="member-info">
                  <span className="member-name">{v.name}</span>
                  <span className="member-email">{v.email}</span>
                </div>
                {canManageMembers && v.id !== currentUser?.id && (
                  <button className="btn-danger-sm" onClick={() => removeViewer(v.id)}>✕</button>
                )}
                {v.id === currentUser?.id && <span className="badge-you">Jij</span>}
              </li>
            ))}
            {group.viewer_contacts?.map((vc) => (
              <li key={`c-${vc.contact_id}`} className="member-item">
                <div className="member-avatar admin-avatar" style={{ background: "var(--surface-hover)" }}>
                  <span>{(vc.first_name[0] ?? "") + (vc.last_name[0] ?? "")}</span>
                </div>
                <div className="member-info">
                  <span className="member-name">
                    {formatContactDisplayName(vc)}
                  </span>
                  {vc.email && <span className="member-email">{vc.email}</span>}
                  <span
                    className="group-badge"
                    style={{ marginTop: 4, display: "inline-block", fontSize: "0.7rem" }}
                  >
                    Alleen medewerker-kaart
                  </span>
                </div>
                {canManageMembers && (
                  <button
                    className="btn-danger-sm"
                    onClick={() => removeViewerContact(vc.contact_id)}
                  >✕</button>
                )}
                {currentUser &&
                  currentUser.contact_id != null &&
                  vc.contact_id === currentUser.contact_id && <span className="badge-you">Jij (kaart)</span>}
              </li>
            ))}
          </ul>
        </div>
        </div>
      </div>

      {showShare && (
        <ShareModal
          groupId={group.id}
          members={group.members ?? []}
          onClose={() => setShowShare(false)}
        />
      )}

      {addLidKindModal && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => !ledenAddBusy && setAddLidKindModal(null)}
          onKeyDown={(e) => e.key === "Escape" && !ledenAddBusy && setAddLidKindModal(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Rol in adresboek kiezen"
        >
          <div
            className="detail-section"
            style={{ maxWidth: 480, margin: 16, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="section-title" style={{ marginTop: 0 }}>
              Adresboek-rol kiezen
            </h3>
            {ledenAddMsg && (
              <p className="page-subtitle" style={{ color: "var(--danger, #b00020)", marginBottom: "0.75rem" }} role="alert">
                {ledenAddMsg}
              </p>
            )}
            <p className="page-subtitle">
              <strong>{formatContactDisplayName(addLidKindModal)}</strong> staat nu als{" "}
              <strong>{contactAddressBookGroupLabel(addLidKindModal.entity_kind)}</strong> in het adresboek. Groepsleden
              horen bij <strong>vrijwilligers</strong> of <strong>personeel</strong>. Kies welke lijst deze persoon
              voortaan als hoofdtype krijgt (de kaart wordt direct bijgewerkt en daarna als lid toegevoegd). Het huidige
              type blijft als <strong>extra label</strong> op de kaart staan (bijv. dominicusganger), zodat die lijsten
              ongewijzigd blijven werken.
            </p>
            <p style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-primary"
                disabled={ledenAddBusy}
                onClick={() => void performAddGroupMember(addLidKindModal.id, "vrijwilliger")}
              >
                {ledenAddBusy ? "Bezig…" : "Vrijwilliger"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={ledenAddBusy}
                onClick={() => void performAddGroupMember(addLidKindModal.id, "personeel")}
              >
                {ledenAddBusy ? "Bezig…" : "Personeel"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={ledenAddBusy}
                onClick={() => setAddLidKindModal(null)}
              >
                Annuleer
              </button>
            </p>
          </div>
        </div>
      )}

      {chairPromotePending && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => !chairSetBusy && setChairPromotePending(null)}
          onKeyDown={(e) => e.key === "Escape" && !chairSetBusy && setChairPromotePending(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Voorzitter: rol in adresboek"
        >
          <div
            className="detail-section"
            style={{ maxWidth: 480, margin: 16, padding: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="section-title" style={{ marginTop: 0 }}>
              Adresboek-rol voor voorzitter
            </h3>
            <p className="page-subtitle">
              <strong>{formatContactDisplayName(chairPromotePending.contactForLabel)}</strong> staat nu als{" "}
              <strong>{contactAddressBookGroupLabel(chairPromotePending.contactForLabel.entity_kind)}</strong> in het
              adresboek. Als voorzitter moet de kaart bij <strong>vrijwilligers</strong> of <strong>personeel</strong>{" "}
              horen. Kies welke lijst deze persoon als hoofdtype krijgt; daarna wordt de voorzitter ingesteld. Het
              huidige type blijft als <strong>extra label</strong> op de kaart (zo blijft bijv. de dominicusgangers-lijst
              correct).
            </p>
            <p style={{ marginTop: 16, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                className="btn-primary"
                disabled={chairSetBusy}
                onClick={() =>
                  void performSetChairRequest({
                    ...chairPromotePending.payload,
                    entity_kind_on_promote: "vrijwilliger",
                  })
                }
              >
                {chairSetBusy ? "Bezig…" : "Vrijwilliger"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={chairSetBusy}
                onClick={() =>
                  void performSetChairRequest({
                    ...chairPromotePending.payload,
                    entity_kind_on_promote: "personeel",
                  })
                }
              >
                {chairSetBusy ? "Bezig…" : "Personeel"}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={chairSetBusy}
                onClick={() => setChairPromotePending(null)}
              >
                Annuleer
              </button>
            </p>
          </div>
        </div>
      )}

      {memberView && (
        <ContactViewModal
          contact={memberView}
          apiPath={contactListApiPath(memberView.entity_kind)}
          title={contactModalTitle(memberView.entity_kind)}
          showEditButton={false}
          onClose={() => setMemberView(null)}
        />
      )}

      {showIntakeModal && group && (
        <SendWerkgroepIntakeModal
          preselectedGroup={{ id: group.id, name: group.name }}
          onClose={() => setShowIntakeModal(false)}
          onSent={() => setShowIntakeModal(false)}
        />
      )}
    </div>
  );
}
